import { DeploymentEnvironment, getRuntimeConfig } from '@hclinear/hc-linear-react';
import { deploymentBorderColors } from '@style/theme';
import { PermissionType } from '@/type';
import { APP_ROUTES } from '@/util';

export type SubMenuItem = {
  title: string;
  url: string;
  roles: PermissionType[];
  icon: string;
  subMenu?: SubMenuItem[];
};

export type MenuItem = {
  id: number;
  key: string;
  name: string;
  serial: number;
  roles?: PermissionType[];
  subMenu?: SubMenuItem[];
  url?: string;
};

export const getNavbarBorder = (): React.CSSProperties => {
  if (getRuntimeConfig().DEPLOYMENT_ENVIRONMENT === DeploymentEnvironment.PROD) {
    return {};
  }

  return {
    borderBottom: `5px solid ${deploymentBorderColors[getRuntimeConfig().DEPLOYMENT_ENVIRONMENT]}`,
  };
};

export const menuItemsNew = (dashboardViewPermission: boolean): MenuItem[] => [
  {
    id: 0,
    key: dashboardViewPermission
      ? 'navbar.submenu.dashboard-device-control'
      : 'navbar.submenu.dashboard',
    name: dashboardViewPermission
      ? 'navbar.submenu.dashboard-device-control'
      : 'navbar.submenu.dashboard',
    serial: 0,
    roles: [
      PermissionType.PERM_MENU_DASHBOARD_VIEW,
      PermissionType.PERM_MENU_DEVICE_CONTROL_VIEW,
    ],
    url: APP_ROUTES.DASHBOARD,
  },
  {
    id: 1,
    key: 'menu-items.1',
    name: 'menu_monitoring_view',
    serial: 1,
    roles: [PermissionType.PERM_MENU_MONITORING_VIEW],
    subMenu: [
      {
        title: 'sidebar.menu.stop-map',
        url: APP_ROUTES.MONITORING.STOP_MAP,
        roles: [PermissionType.PERM_MENU_MAP_VIEW],
        icon: 'Map',
      },
      {
        title: 'navbar.submenu.stop-terminal-status',
        url: APP_ROUTES.MONITORING.STOP_TERMINAL_STATUS,
        roles: [PermissionType.PERM_MENU_TERMINAL_STATUSES_VIEW],
        icon: 'ConnectedTv',
      },
      {
        title: 'navbar.submenu.data-source',
        url: APP_ROUTES.MONITORING.TIMETABLE_DATA,
        roles: [PermissionType.PERM_MENU_TIMETABLE_SOURCE_DATA_VIEW],
        icon: 'Feed',
      },
      {
        title: 'navbar.submenu.product-log',
        url: APP_ROUTES.MONITORING.PRODUCT_LOG,
        roles: [PermissionType.PERM_MENU_PRODUCT_LOGS_VIEW],
        icon: 'Adb',
      },
      {
        title: 'navbar.submenu.product-console',
        url: APP_ROUTES.MONITORING.PRODUCT_CONSOLE,
        roles: [PermissionType.PERM_MENU_PRODUCT_CONSOLE_VIEW],
        icon: 'Terminal',
      },
    ],
  },
  {
    id: 2,
    key: 'menu-items.2',
    name: 'menu_announcement_view',
    serial: 2,
    roles: [
      PermissionType.PERM_MENU_ANNOUNCEMENT_VIEW,
      PermissionType.PERM_MENU_FLAG_VIEW,
    ],
    subMenu: [
      {
        title: 'navbar.submenu.announcements-list',
        url: APP_ROUTES.ANNOUNCEMENTS.ANNOUNCEMENTS,
        roles: [PermissionType.PERM_MENU_ANNOUNCEMENT_VIEW],
        icon: 'Campaign',
      },
      {
        title: 'navbar.submenu.flags',
        url: APP_ROUTES.ANNOUNCEMENTS.FLAG,
        roles: [PermissionType.PERM_MENU_FLAG_VIEW],
        icon: 'Flag',
      },
    ],
  },
  {
    id: 3,
    key: 'menu-items.3',
    name: 'menu_real_view',
    serial: 3,
    roles: [PermissionType.PERM_MENU_REAL_VIEW],
    subMenu: [
      {
        title: 'navbar.submenu.stop-list',
        url: APP_ROUTES.REAL_VIEW.STOP_LIST,
        roles: [PermissionType.PERM_MENU_STOP_VIEW],
        icon: 'ListAlt',
      },
      {
        title: 'navbar.submenu.product-list',
        url: APP_ROUTES.REAL_VIEW.PRODUCT_LIST,
        roles: [PermissionType.PERM_MENU_PRODUCT_SN_VIEW],
        icon: 'CastConnected',
      },
    ],
  },
  {
    id: 4,
    key: 'menu-items.4',
    name: 'menu_template_view',
    serial: 4,
    roles: [PermissionType.PERM_MENU_TEMPLATE_VIEW],
    subMenu: [
      {
        title: 'navbar.submenu.images-list',
        url: APP_ROUTES.TEMPLATES.IMAGES,
        roles: [PermissionType.PERM_MENU_IMAGE_VIEW],
        icon: 'ImageSharp',
      },
      {
        title: 'navbar.submenu.layouts',
        url: APP_ROUTES.TEMPLATES.LAYOUTS,
        roles: [PermissionType.PERM_MENU_LAYOUT_VIEW],
        icon: 'DashboardCustomize',
      },
      {
        title: 'navbar.submenu.dynamicTimeTable',
        url: APP_ROUTES.TEMPLATES.TIMETABLES,
        roles: [PermissionType.PERM_MENU_DYNAMIC_TIMETABLE_VIEW],
        icon: 'LineStyle',
      },
      {
        title: 'navbar.submenu.operational-schemes',
        url: APP_ROUTES.TEMPLATES.OPERATIONAL_SCHEMES,
        roles: [PermissionType.PERM_MENU_STATE_VIEW],
        icon: 'AddAlarmSharp',
      },
      {
        title: 'navbar.submenu.stop-template-list',
        url: APP_ROUTES.TEMPLATES.STOP_TEMPLATE,
        roles: [PermissionType.PERM_MENU_STOP_TEMPLATE_VIEW],
        icon: 'MyLocation',
      },
      {
        title: 'navbar.submenu.font-list',
        url: APP_ROUTES.TEMPLATES.FONT_LIST,
        roles: [PermissionType.PERM_MENU_RASTER_FONT_VIEW],
        icon: 'FontDownload',
      },
      {
        title: 'navbar.submenu.displays-list',
        url: APP_ROUTES.TEMPLATES.DISPLAY_LIST,
        roles: [PermissionType.PERM_MENU_DISPLAY_VIEW],
        icon: 'ConnectedTv',
      },
      {
        title: 'dictionary.page.name',
        url: APP_ROUTES.TEMPLATES.DICTIONARY,
        roles: [PermissionType.PERM_MENU_SCHEDULE_DICTIONARY_VIEW],
        icon: 'AutoStories',
      },
    ],
  },
  {
    id: 5,
    key: 'menu-items.5',
    name: 'menu_system_view',
    serial: 5,
    roles: [PermissionType.PERM_MENU_SYSTEM_VIEW],
    subMenu: [
      {
        title: 'navbar.submenu.groups-list',
        url: APP_ROUTES.SYSTEM.GROUPS,
        roles: [PermissionType.PERM_MENU_GROUP_VIEW],
        icon: 'Groups',
      },
      {
        title: 'navbar.submenu.company-list',
        url: APP_ROUTES.SYSTEM.COMPANIES,
        roles: [PermissionType.PERM_MENU_COMPANY_VIEW],
        icon: 'Business',
      },
      {
        title: 'navbar.submenu.user-list',
        url: APP_ROUTES.SYSTEM.USERS,
        roles: [PermissionType.PERM_MENU_USER_VIEW],
        icon: 'PeopleOutline',
      },
      {
        title: 'navbar.submenu.server-list',
        url: APP_ROUTES.SYSTEM.SERVER_LIST,
        roles: [PermissionType.PERM_MENU_SERVER_VIEW],
        icon: 'DnsOutlined',
      },
    ],
  },
  {
    id: 6,
    key: 'menu-items.6',
    name: 'menu_firmware_view',
    serial: 6,
    roles: [PermissionType.PERM_MENU_FIRMWARE_VIEW],
    subMenu: [
      {
        title: 'navbar.submenu.firmware',
        url: APP_ROUTES.FIRMWARE.FIRMWARE_LIST,
        roles: [PermissionType.PERM_MENU_FIRMWARE_VIEW],
        icon: 'FlashOnOutlined',
      },
      {
        title: 'navbar.submenu.firmware-update-list',
        url: APP_ROUTES.FIRMWARE.FIRMWARE_TERMINAL_UPDATE_LIST,
        roles: [PermissionType.PERM_MENU_FIRMWARE_VIEW],
        icon: 'SyncLockTwoTone',
      },
      {
        title: 'navbar.submenu.hardware-version-list',
        url: APP_ROUTES.FIRMWARE.HARDWARE_VERSIONS,
        roles: [PermissionType.PERM_MENU_HARDWARE_VERSION_VIEW],
        icon: 'Memory',
      },
    ],
  },
];
